import { relatedToolsMarkup, siteBrandMarkup } from '../site-chrome';
import { FateCameraController } from './camera';
import { MarbleRaceEngine, participantColor } from './core';
import {
  MAX_MARBLE_COUNT,
  expandParticipantGroups,
  marbleDisplayName,
  mergeParticipantGroup,
  parseParticipantGroups,
  participantTotal,
  setAllParticipantCounts,
  updateParticipantGroup,
  type MarbleEntry,
  type ParticipantGroup,
} from './input';
import { DEFAULT_FATE_MAP_ID, FATE_MAPS, normalizeMapSelection, resolveMap } from './maps';
import { mapPreviewMarkup } from './map-preview';
import { clientToCanvasPoint, createMiniMapLayout, miniMapToWorld } from './minimap';
import { createSecureSeed, shortSeed } from './random';
import { preparePhysicsReplay } from './replay';
import { FateMarbleRenderer } from './renderer';
import { createFrameProfile } from './frame-profile';
import type { CameraTargetSelection, FateMapSelection, WinMode } from './types';

const SPEEDS = [1, 2, 4] as const;
export type RaceSpeed = typeof SPEEDS[number];

export type RaceShortcut = 'speed' | 'pause' | null;

export function resolveRaceShortcut(
  event: Pick<KeyboardEvent, 'code' | 'key' | 'repeat'>,
): RaceShortcut {
  if (event.repeat) return null;
  if (event.code === 'KeyF' || event.key.toLowerCase() === 'f' || event.key === 'ㄹ') return 'speed';
  if (event.code === 'Space' || event.key === ' ') return 'pause';
  return null;
}

export function speedAfterTargetFinish(current: RaceSpeed, remainingCount: number): RaceSpeed {
  return current === 1 && remainingCount > 0 ? 4 : current;
}

export interface RaceFrameTiming {
  readonly realDelta: number;
}

export function raceFrameTiming(previousFrameAt: number, now: number): RaceFrameTiming {
  const realDelta = Number.isFinite(previousFrameAt) && Number.isFinite(now)
    ? Math.max(0, (now - previousFrameAt) / 1000)
    : 0;
  return { realDelta };
}

export interface RaceFrameAdvance {
  readonly nextSpeed: RaceSpeed;
  readonly targetJustFinished: boolean;
}

export function advanceRaceFrame(
  engine: MarbleRaceEngine,
  realDelta: number,
  currentSpeed: RaceSpeed,
): RaceFrameAdvance {
  const safeDelta = Number.isFinite(realDelta) ? Math.max(0, realDelta) : 0;
  const targetWasPending = engine.finishers.length < engine.targetRank;
  const secondsUntilTarget = Math.max(0, engine.targetMarble.targetFinishTime - engine.elapsed);

  // 1× 자동 전환 프레임은 당첨 시각까지 1×로 진행한 뒤, 같은 RAF의 남은
  // 실제 시간을 즉시 4×로 재생한다. 저FPS에서도 전환이 한 프레임 늦지 않는다.
  if (targetWasPending && currentSpeed === 1 && secondsUntilTarget <= safeDelta) {
    engine.step(secondsUntilTarget);
    const targetJustFinished = engine.finishers.length >= engine.targetRank;
    const nextSpeed = targetJustFinished
      ? speedAfterTargetFinish(currentSpeed, engine.remaining.length)
      : currentSpeed;
    engine.step((safeDelta - secondsUntilTarget) * nextSpeed);
    return { nextSpeed, targetJustFinished };
  }

  engine.step(safeDelta * currentSpeed);
  const targetJustFinished = targetWasPending && engine.finishers.length >= engine.targetRank;
  const nextSpeed = targetJustFinished
    ? speedAfterTargetFinish(currentSpeed, engine.remaining.length)
    : currentSpeed;
  return { nextSpeed, targetJustFinished };
}

interface SetupSnapshot {
  readonly groups: readonly ParticipantGroup[];
  readonly entries: readonly MarbleEntry[];
  readonly mapSelection: FateMapSelection;
  readonly winMode: WinMode;
  readonly requestedRank?: number;
  readonly eventsEnabled: boolean;
}

const DEFAULT_GROUPS: readonly ParticipantGroup[] = [
  { name: '홍길동', count: 10 },
  { name: '김영희', count: 5 },
  { name: '박철수', count: 3 },
  { name: '이오늘', count: 2 },
];

function requireElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`${selector} 요소를 찾지 못했습니다.`);
  return element;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  })[character]!);
}

function ordinalCopy(rank: number): string {
  if (rank === 1) return '첫 번째';
  return `${rank}번째`;
}

function ruleCopy(mode: WinMode, targetRank: number): string {
  if (mode === 'first') return '첫 번째 도착 마블이 당첨';
  if (mode === 'last') return '마지막 도착 마블이 당첨';
  return `${targetRank}번째 도착 마블이 당첨`;
}

export function resultFinishersMarkup(currentEngine: MarbleRaceEngine): string {
  const finishers = [...currentEngine.finishers].sort((left, right) => left.finishRank - right.finishRank);
  return finishers.map((marble) => `<li class="${marble.id === currentEngine.targetMarble.id ? 'winner' : ''}">
    <span style="--marble-color:${escapeHtml(marble.color)}">${marble.finishRank}</span><strong>${escapeHtml(marbleDisplayName(marble))}</strong>${marble.id === currentEngine.targetMarble.id ? '<b>당첨</b>' : ''}
  </li>`).join('');
}

export function resultProgressCopy(currentEngine: MarbleRaceEngine): string {
  return currentEngine.remaining.length > 0
    ? `당첨 확정 · 나머지 ${currentEngine.remaining.length}개 마블은 계속 달리고 있어요.`
    : `${currentEngine.marbles.length}개 마블이 모두 도착했어요.`;
}

export function standingRowsForEngine(
  currentEngine: MarbleRaceEngine,
  selection: CameraTargetSelection,
): MarbleEntry[] {
  const ordered = currentEngine.currentOrder();
  const chosenIds = new Set<string>();
  currentEngine.finishers.slice(-2).forEach((marble) => chosenIds.add(marble.id));
  [...selection.highlightedIds, ...selection.ids].forEach((id) => {
    if (chosenIds.size < 7) chosenIds.add(id);
  });
  if (chosenIds.size < 5) ordered.slice(0, 5).forEach((marble) => chosenIds.add(marble.id));
  return ordered.filter((marble) => chosenIds.has(marble.id)).slice(0, 7);
}

export function mapStartHint(_selection: FateMapSelection): string {
  return '3초 카운트다운 · 경기 중 1·2·4배속 조절';
}

export function mapOptionsMarkup(): string {
  const choices = [
    ...FATE_MAPS,
    { id: 'random', title: '랜덤 맵', watchPoint: `${FATE_MAPS.length}종 중 무작위`, description: `${FATE_MAPS.length}가지 맵 중 하나를 출발 전에 공개해요.` },
  ];
  return choices.map((map) => `
    <label class="fate-map-option" data-map-id="${map.id}">
      <input type="radio" name="map" value="${map.id}"${map.id === DEFAULT_FATE_MAP_ID ? ' checked' : ''} />
      <span class="fate-map-check" aria-hidden="true">✓</span>
      <span class="fate-map-thumb" aria-hidden="true">${'field' in map ? mapPreviewMarkup(map) : '<span class="fate-map-random-mark">?</span>'}</span>
      <strong>${escapeHtml(map.title)}</strong>
      <small>${escapeHtml(map.watchPoint)}</small>
      <p>${escapeHtml(map.description)}</p>
    </label>`).join('');
}

export function mountFateMarble(): void {
  const app = requireElement<HTMLDivElement>('#app');
  document.documentElement.classList.add('fate-marble-page');
  document.title = '운명의 마블 | 오늘툴';
  app.innerHTML = `
    <div id="fate-shell" class="fate-shell">
      <header class="fate-header">${siteBrandMarkup()}<span>2–100개 · 로컬 복불복</span></header>
      <main>
        <section class="fate-intro">
          <span>RACE OF DESTINY</span>
          <h1>운명의 마블</h1>
          <p>이름을 넣고 지켜보세요. 반전 가득한 트랙 끝에서 지정한 순위의 마블이 당첨됩니다.</p>
        </section>

        <section id="fate-card" class="fate-card" aria-label="운명의 마블 게임">
          <form id="fate-setup" class="fate-setup" novalidate>
            <div class="fate-setup-grid">
              <section class="fate-setup-section fate-names-section">
                <div class="fate-section-heading">
                  <span>1</span><div><h2>참가자</h2><p>이름과 마블 수를 넣고 추가하세요.</p></div>
                </div>
                <div class="fate-participant-composer">
                  <label for="fate-name"><span>이름</span><input id="fate-name" type="text" maxlength="16" autocomplete="off" placeholder="이름 (최대 16자)" /></label>
                  <div class="fate-quantity-field" aria-label="추가할 마블 수">
                    <button type="button" data-composer-step="-1" aria-label="마블 수 줄이기">−</button>
                    <input id="fate-quantity" type="text" value="1" inputmode="numeric" pattern="[0-9]*" autocomplete="off" data-select-all-number role="spinbutton" aria-valuemin="1" aria-valuemax="100" aria-valuenow="1" aria-label="마블 수" />
                    <button type="button" data-composer-step="1" aria-label="마블 수 늘리기">+</button>
                  </div>
                  <button id="fate-add-participant" class="fate-add-participant" type="button">추가</button>
                </div>
                <p id="fate-participant-status" class="fate-participant-status" role="status" aria-live="polite"></p>
                <div id="fate-participant-list" class="fate-participant-list" aria-label="추가된 참가자"></div>
                <div class="fate-participant-summary">
                  <div><strong id="fate-participant-total">참가자 0명 · 마블 0 / 100</strong><span id="fate-participant-remaining">여유 100개</span></div>
                  <div class="fate-quick-counts" aria-label="모든 참가자 마블 수 설정">
                    <span>모두</span>${[1, 2, 3, 5, 10].map((count) => `<button type="button" data-all-count="${count}">${count}개</button>`).join('')}
                  </div>
                  <button id="fate-open-paste" class="fate-paste-open" type="button">여러 명 붙여넣기</button>
                </div>
              </section>

              <div class="fate-option-stack">
                <section class="fate-setup-section fate-map-section">
                  <div class="fate-section-heading">
                    <span>2</span><div><h2>맵 선택</h2><p>${FATE_MAPS.length}가지 트랙 · 시작 위치는 매번 무작위예요.</p></div>
                  </div>
                  <div class="fate-map-options">${mapOptionsMarkup()}</div>
                </section>

                <section class="fate-setup-section fate-rank-section">
                  <div class="fate-section-heading">
                    <span>3</span><div><h2>당첨 순위</h2><p>카메라도 이 기준에 맞춰 움직여요.</p></div>
                  </div>
                  <div class="fate-rank-options">
                    <label><input type="radio" name="win-mode" value="first" checked /><span><strong>첫 번째</strong><small>가장 먼저 도착</small></span></label>
                    <label><input type="radio" name="win-mode" value="last" /><span><strong>마지막</strong><small>가장 늦게 도착</small></span></label>
                    <label><input type="radio" name="win-mode" value="rank" /><span><strong>특정 순위</strong><small>원하는 도착 순서</small></span></label>
                  </div>
                  <label id="fate-rank-field" class="fate-rank-field" for="fate-rank" hidden>
                    <span>당첨될 도착 순위</span>
                    <input id="fate-rank" type="text" value="7" inputmode="numeric" pattern="[0-9]*" autocomplete="off" data-select-all-number role="spinbutton" aria-valuemin="1" aria-valuenow="7" />
                    <small id="fate-rank-hint">1부터 전체 마블 수 사이로 입력하세요.</small>
                  </label>
                </section>

                <section class="fate-setup-section fate-toggle-section">
                  <label class="fate-event-toggle">
                    <input id="fate-events" type="checkbox" checked />
                    <span aria-hidden="true"></span>
                    <div><strong>반전 이벤트</strong><small>공이 접근한 장치에서 최대 3번 반전이 일어나요.</small></div>
                  </label>
                </section>
              </div>
            </div>

            <div class="fate-setup-actions">
              <p id="fate-error" class="fate-error" role="alert"></p>
              <button id="fate-start" class="fate-primary-button" type="submit"><span>레이스 시작</span><small id="fate-start-hint">${mapStartHint(DEFAULT_FATE_MAP_ID)}</small></button>
            </div>

            <section id="fate-paste-sheet" class="fate-paste-sheet" hidden aria-label="여러 명 붙여넣기">
              <div>
                <header><div><strong>여러 명 한꺼번에 붙여넣기</strong><small>X·×·* 배수 문법은 여기서만 사용해요.</small></div><button id="fate-close-paste" type="button" aria-label="닫기">×</button></header>
                <textarea id="fate-paste-input" rows="7" spellcheck="false" placeholder="홍길동 X 10\n김영희 5\n박철수"></textarea>
                <p>쉼표·줄바꿈 구분 · 이름 뒤 숫자가 없으면 1개</p>
                <div id="fate-paste-preview" class="fate-paste-preview" aria-live="polite">명단을 붙여넣으면 미리 보여드려요.</div>
                <footer><button id="fate-cancel-paste" type="button">취소</button><button id="fate-apply-paste" type="button" disabled>목록에 추가</button></footer>
              </div>
            </section>
          </form>

          <section id="fate-race" class="fate-race" hidden aria-label="마블 레이스">
            <div class="fate-hud">
              <div><small>맵</small><strong id="fate-map-name">${escapeHtml(FATE_MAPS[0].title)}</strong><span id="fate-lap-badge" class="fate-lap-badge" hidden></span></div>
              <div class="fate-rule"><small>당첨 기준</small><strong id="fate-rule">첫 번째 도착</strong></div>
              <div><small>경기 시간</small><strong id="fate-time">0.0초</strong></div>
              <div><small>도착</small><strong id="fate-finished">0 / 0</strong></div>
              <button id="fate-speed" class="fate-hud-button" type="button" aria-label="빨리감기, 현재 1배속"><span>빨리감기</span><strong>1×</strong></button>
            </div>

            <p class="fate-orientation-hint">휴대폰을 가로로 돌리면 충돌과 추월이 더 크게 보여요.</p>
            <div class="fate-race-layout">
              <div id="fate-stage" class="fate-stage">
                <canvas id="fate-canvas" width="960" height="600" role="img" aria-label="운명의 마블 레이스 화면" aria-describedby="fate-minimap-help"></canvas>
                <span id="fate-minimap-help" class="sr-only">우측 상단 전체 트랙에 마우스를 올리면 해당 구간을 둘러볼 수 있습니다.</span>
                <div id="fate-countdown" class="fate-countdown" aria-live="assertive"><small>READY</small><strong>3</strong><span></span></div>
                <div id="fate-event-banner" class="fate-event-banner" hidden aria-live="polite"><small></small><strong></strong><span></span></div>
                <div id="fate-lap-notice" class="fate-lap-notice" hidden aria-live="polite"></div>
                <div id="fate-notice" class="fate-race-notice" hidden></div>
                <div id="fate-pause-overlay" class="fate-pause-overlay" hidden>
                  <small>PAUSED</small><strong>레이스가 잠시 멈췄어요</strong><span>돌아온 뒤 이어서 볼 수 있어요.</span>
                  <button id="fate-resume" type="button">이어서 보기</button>
                </div>
                <section id="fate-result" class="fate-result" hidden aria-live="assertive"></section>
              </div>

              <aside class="fate-standings" aria-labelledby="fate-standings-title">
                <header><div><small>LIVE</small><h2 id="fate-standings-title">선두 경쟁</h2></div><strong id="fate-remaining">9개 남음</strong></header>
                <div id="fate-standing-list" class="fate-standing-list"></div>
                <p id="fate-screen-reader-status" class="fate-screen-reader-status" aria-live="polite"></p>
              </aside>
            </div>

            <div class="fate-race-controls">
              <button id="fate-pause" type="button">일시정지</button>
              <button id="fate-fullscreen" type="button">전체 화면</button>
              <button id="fate-back" type="button">이름·설정 수정</button>
              <span><kbd>F</kbd> 빨리감기 · <kbd>Space</kbd> 일시정지</span>
            </div>
          </section>
        </section>

        <section class="fate-help" aria-label="게임 방법">
          <article><span>01</span><strong>명단과 순위를 선택</strong><p>같은 이름에 여러 마블을 주거나 원하는 당첨 순위를 직접 정할 수 있어요.</p></article>
          <article><span>02</span><strong>배속을 직접 선택</strong><p>1×·2×·4×로 관전하고, 당첨 순위가 나온 뒤 1×라면 남은 완주는 4×로 진행해요.</p></article>
          <article><span>03</span><strong>후보를 따라가는 카메라</strong><p>전체 미니맵을 보면서 당첨 순위에 중요한 마블 묶음을 자동 추적해요.</p></article>
        </section>

        ${relatedToolsMarkup('fate-marble')}
      </main>
      <footer class="fate-footer"><a href="/privacy/">개인정보처리방침</a><a href="/terms/">이용약관</a><span>입력한 이름과 결과는 현재 브라우저에서만 처리돼요.</span></footer>
    </div>`;

  const shell = requireElement<HTMLDivElement>('#fate-shell');
  const card = requireElement<HTMLElement>('#fate-card');
  const form = requireElement<HTMLFormElement>('#fate-setup');
  const composerName = requireElement<HTMLInputElement>('#fate-name');
  const composerQuantity = requireElement<HTMLInputElement>('#fate-quantity');
  const addParticipantButton = requireElement<HTMLButtonElement>('#fate-add-participant');
  const participantStatus = requireElement<HTMLParagraphElement>('#fate-participant-status');
  const participantList = requireElement<HTMLDivElement>('#fate-participant-list');
  const participantTotalCopy = requireElement<HTMLElement>('#fate-participant-total');
  const participantRemaining = requireElement<HTMLElement>('#fate-participant-remaining');
  const pasteSheet = requireElement<HTMLElement>('#fate-paste-sheet');
  const pasteInput = requireElement<HTMLTextAreaElement>('#fate-paste-input');
  const pastePreview = requireElement<HTMLDivElement>('#fate-paste-preview');
  const applyPasteButton = requireElement<HTMLButtonElement>('#fate-apply-paste');
  const error = requireElement<HTMLParagraphElement>('#fate-error');
  const startButton = requireElement<HTMLButtonElement>('#fate-start');
  const rankField = requireElement<HTMLElement>('#fate-rank-field');
  const rankInput = requireElement<HTMLInputElement>('#fate-rank');
  const rankHint = requireElement<HTMLElement>('#fate-rank-hint');
  const race = requireElement<HTMLElement>('#fate-race');
  const stage = requireElement<HTMLDivElement>('#fate-stage');
  const canvas = requireElement<HTMLCanvasElement>('#fate-canvas');
  const renderer = new FateMarbleRenderer(canvas);
  const camera = new FateCameraController();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const mapName = requireElement<HTMLElement>('#fate-map-name');
  const rule = requireElement<HTMLElement>('#fate-rule');
  const time = requireElement<HTMLElement>('#fate-time');
  const finished = requireElement<HTMLElement>('#fate-finished');
  const speedButton = requireElement<HTMLButtonElement>('#fate-speed');
  const speedValue = requireElement<HTMLElement>('#fate-speed strong');
  const countdown = requireElement<HTMLDivElement>('#fate-countdown');
  const countdownValue = requireElement<HTMLElement>('#fate-countdown strong');
  const countdownMap = requireElement<HTMLElement>('#fate-countdown span');
  const eventBanner = requireElement<HTMLDivElement>('#fate-event-banner');
  const lapBadge = requireElement<HTMLSpanElement>('#fate-lap-badge');
  const lapNotice = requireElement<HTMLDivElement>('#fate-lap-notice');
  const raceNotice = requireElement<HTMLDivElement>('#fate-notice');
  const pauseOverlay = requireElement<HTMLDivElement>('#fate-pause-overlay');
  const resultPanel = requireElement<HTMLElement>('#fate-result');
  const standingsTitle = requireElement<HTMLElement>('#fate-standings-title');
  const remaining = requireElement<HTMLElement>('#fate-remaining');
  const standingList = requireElement<HTMLDivElement>('#fate-standing-list');
  const screenReaderStatus = requireElement<HTMLParagraphElement>('#fate-screen-reader-status');

  let participantGroups: ParticipantGroup[] = DEFAULT_GROUPS.map((group) => ({ ...group }));
  let pasteGroups: ParticipantGroup[] | null = null;
  let engine: MarbleRaceEngine | null = null;
  let lastSnapshot: SetupSnapshot | null = null;
  let speed: RaceSpeed = 1;
  let paused = false;
  let countdownEndsAt = 0;
  let pausedCountdownRemaining = 0;
  let lastFrameAt = performance.now();
  let resultReachedAt: number | null = null;
  let resultVisible = false;
  let lastHudAt = 0;
  let lastStandingMarkup = '';
  let lastFinishedCount = 0;
  let latestSelection: CameraTargetSelection = { ids: [], highlightedIds: [], mode: 'start' };
  let lastEventKey = '';
  let eventHoldUntil = 0;
  let presentedLap = 1;
  let lapNoticeUntil = 0;
  let noticeTimer = 0;
  let preparationId = 0;
  let miniMapInspecting = false;
  const committedStandingRanks = new Map<string, number>();
  const pendingStandingRanks = new Map<string, { rank: number; since: number }>();
  const standingRankChanges = new Map<string, { delta: number; until: number }>();

  function selectedWinMode(): WinMode {
    return requireElement<HTMLInputElement>('input[name="win-mode"]:checked').value as WinMode;
  }

  function selectedMap(): FateMapSelection {
    return normalizeMapSelection(requireElement<HTMLInputElement>('input[name="map"]:checked').value);
  }

  function setParticipantMessage(copy: string, tone: 'normal' | 'error' | 'success' = 'normal'): void {
    participantStatus.textContent = copy;
    participantStatus.dataset.tone = tone;
  }

  function renderParticipants(highlightIndex = -1): void {
    const total = participantTotal(participantGroups);
    participantTotalCopy.textContent = `참가자 ${participantGroups.length}명 · 마블 ${total} / ${MAX_MARBLE_COUNT}`;
    participantRemaining.textContent = `여유 ${MAX_MARBLE_COUNT - total}개`;
    participantList.innerHTML = participantGroups.length === 0
      ? '<div class="fate-participant-empty"><strong>아직 참가자가 없어요</strong><span>이름을 입력하고 추가를 누르면 마블이 생겨요.</span></div>'
      : participantGroups.map((group, index) => {
        const probability = total > 0 ? group.count / total * 100 : 0;
        return `<div class="fate-participant-row${index === highlightIndex ? ' merged' : ''}" data-participant-index="${index}">
          <i style="--participant-color:${participantColor(index)}" aria-hidden="true"></i>
          <label><span class="sr-only">참가자 ${index + 1} 이름</span><input type="text" maxlength="16" value="${escapeHtml(group.name)}" data-group-name="${index}" /></label>
          <span class="fate-probability">당첨 비중 ${probability < 10 ? probability.toFixed(1) : probability.toFixed(0)}%</span>
          <div class="fate-row-stepper">
            <button type="button" data-group-step="-1" aria-label="${escapeHtml(group.name)} 마블 수 줄이기">−</button>
            <input type="text" value="${group.count}" inputmode="numeric" pattern="[0-9]*" autocomplete="off" data-select-all-number data-group-count="${index}" role="spinbutton" aria-valuemin="1" aria-valuemax="100" aria-valuenow="${group.count}" aria-label="${escapeHtml(group.name)} 마블 수" />
            <button type="button" data-group-step="1" aria-label="${escapeHtml(group.name)} 마블 수 늘리기">+</button>
          </div>
          <button class="fate-remove-participant" type="button" data-group-remove="${index}" aria-label="${escapeHtml(group.name)} 삭제">×</button>
        </div>`;
      }).join('');
    rankInput.setAttribute('aria-valuemax', String(Math.max(1, total)));
    rankHint.textContent = total > 0 ? `1 ~ ${total} 사이 입력` : '참가자를 먼저 추가해 주세요.';
    const rankValue = Number(rankInput.value);
    const rankValid = selectedWinMode() !== 'rank'
      || Number.isInteger(rankValue) && rankValue >= 1 && rankValue <= total;
    if (!rankValid && total >= 2) error.textContent = `당첨 순위는 1부터 ${total} 사이로 입력해 주세요.`;
    else error.textContent = '';
    startButton.disabled = total < 2 || !rankValid;
  }

  function replaceGroups(next: readonly ParticipantGroup[], message = '', highlightIndex = -1): void {
    participantGroups = next.map((group) => ({ ...group }));
    renderParticipants(highlightIndex);
    if (message) setParticipantMessage(message, 'success');
  }

  function addParticipant(): void {
    try {
      const previous = participantGroups.find((group) => group.name.toLocaleLowerCase('ko-KR') === composerName.value.trim().toLocaleLowerCase('ko-KR'));
      const merged = mergeParticipantGroup(participantGroups, {
        name: composerName.value,
        count: Number(composerQuantity.value),
      });
      replaceGroups(
        merged.groups,
        previous
          ? `이미 있는 이름이라 기존 ${previous.count}개에 +${merged.added} 합산했어요.`
          : `${merged.groups[merged.mergedIndex].name} 참가자를 추가했어요.`,
        merged.mergedIndex,
      );
      composerName.value = '';
      composerQuantity.value = '1';
      composerQuantity.setAttribute('aria-valuenow', '1');
      composerName.focus();
    } catch (reason) {
      setParticipantMessage(reason instanceof Error ? reason.message : '입력값을 확인해 주세요.', 'error');
      if (!composerName.value.trim()) composerName.focus();
      else composerQuantity.focus();
    }
  }

  function updateGroupAt(index: number, draft: ParticipantGroup, message = ''): void {
    try {
      replaceGroups(updateParticipantGroup(participantGroups, index, draft), message);
    } catch (reason) {
      setParticipantMessage(reason instanceof Error ? reason.message : '수정값을 확인해 주세요.', 'error');
      renderParticipants();
    }
  }

  function renderPastePreview(): void {
    try {
      pasteGroups = parseParticipantGroups(pasteInput.value);
      const total = participantTotal(pasteGroups);
      const combined = total + participantTotal(participantGroups);
      if (combined > MAX_MARBLE_COUNT) throw new Error(`추가하면 마블이 ${combined}개가 돼요. 지금은 ${MAX_MARBLE_COUNT - participantTotal(participantGroups)}개까지 추가할 수 있어요.`);
      pastePreview.classList.remove('invalid');
      pastePreview.innerHTML = `<strong>미리보기 — ${pasteGroups.length}명 · 마블 ${total}개</strong><ul>${pasteGroups.map((group) => `<li><span>${escapeHtml(group.name)}</span><b>${group.count}</b></li>`).join('')}</ul>`;
      applyPasteButton.disabled = false;
    } catch (reason) {
      pasteGroups = null;
      pastePreview.classList.add('invalid');
      pastePreview.textContent = pasteInput.value.trim()
        ? reason instanceof Error ? reason.message : '명단을 확인해 주세요.'
        : '명단을 붙여넣으면 미리 보여드려요.';
      applyPasteButton.disabled = true;
    }
  }

  function closePasteSheet(): void {
    pasteSheet.hidden = true;
    pasteGroups = null;
    pasteInput.value = '';
    renderPastePreview();
  }

  function readSnapshot(): SetupSnapshot {
    const groups = participantGroups.map((group) => ({ ...group }));
    if (participantTotal(groups) < 2) throw new Error('마블은 두 개 이상 필요해요.');
    const entries = expandParticipantGroups(groups);
    const winMode = selectedWinMode();
    const requestedRank = winMode === 'rank' ? Number(rankInput.value) : undefined;
    return {
      groups,
      entries,
      mapSelection: selectedMap(),
      winMode,
      requestedRank,
      eventsEnabled: requireElement<HTMLInputElement>('#fate-events').checked,
    };
  }

  function setSpeed(nextSpeed: RaceSpeed): void {
    speed = nextSpeed;
    speedValue.textContent = `${speed}×`;
    speedButton.setAttribute('aria-label', `빨리감기, 현재 ${speed}배속`);
  }

  function cycleSpeed(): void {
    if (!engine || engine.phase !== 'racing' || paused) return;
    const nextIndex = (SPEEDS.indexOf(speed) + 1) % SPEEDS.length;
    setSpeed(SPEEDS[nextIndex]);
  }

  function showNotice(copy: string, duration = 1100): void {
    window.clearTimeout(noticeTimer);
    raceNotice.textContent = copy;
    raceNotice.hidden = false;
    noticeTimer = window.setTimeout(() => { raceNotice.hidden = true; }, duration);
  }

  function setMiniMapInspecting(active: boolean): void {
    if (miniMapInspecting === active) return;
    miniMapInspecting = active;
    canvas.classList.toggle('is-minimap-inspecting', active);
  }

  function clearMiniMapInspection(): void {
    camera.clearInspectTarget();
    setMiniMapInspecting(false);
  }

  function inspectPointForEvent(event: PointerEvent) {
    if (!engine || race.hidden || paused || resultVisible) return null;
    const bounds = canvas.getBoundingClientRect();
    const point = clientToCanvasPoint(bounds, renderer.width, renderer.height, event.clientX, event.clientY);
    if (!point) return null;
    return miniMapToWorld(engine.map, createMiniMapLayout(engine.map, renderer.width), point.x, point.y);
  }

  function updateMiniMapInspection(event: PointerEvent): boolean {
    const point = inspectPointForEvent(event);
    if (!point) {
      clearMiniMapInspection();
      return false;
    }
    camera.setInspectTarget(point.x, point.y);
    setMiniMapInspecting(true);
    return true;
  }

  async function beginRace(snapshot: SetupSnapshot): Promise<void> {
    const currentPreparationId = ++preparationId;
    window.clearTimeout(noticeTimer);
    lastSnapshot = snapshot;
    engine = null;
    clearMiniMapInspection();
    const seed = createSecureSeed();
    const selectedMapDefinition = resolveMap(snapshot.mapSelection, seed);
    setSpeed(1);
    paused = false;
    resultReachedAt = null;
    resultVisible = false;
    lastFinishedCount = 0;
    lastEventKey = '';
    eventHoldUntil = 0;
    presentedLap = 1;
    lapNoticeUntil = 0;
    lapBadge.hidden = true;
    lapNotice.hidden = true;
    committedStandingRanks.clear();
    pendingStandingRanks.clear();
    standingRankChanges.clear();
    resultPanel.hidden = true;
    resultPanel.innerHTML = '';
    stage.classList.remove('result-visible');
    pauseOverlay.hidden = true;
    eventBanner.hidden = true;
    raceNotice.hidden = true;
    form.hidden = true;
    race.hidden = false;
    race.setAttribute('aria-busy', 'true');
    shell.classList.add('race-running');
    mapName.textContent = selectedMapDefinition.title;
    const requestedTargetRank = snapshot.winMode === 'first'
      ? 1
      : snapshot.winMode === 'last' ? snapshot.entries.length : snapshot.requestedRank ?? 1;
    rule.textContent = ruleCopy(snapshot.winMode, requestedTargetRank);
    countdownMap.textContent = `${selectedMapDefinition.title} · 트랙 준비 중`;
    countdown.hidden = false;
    countdownValue.textContent = '…';
    screenReaderStatus.textContent = '익명 물리 트랙을 준비하고 있습니다.';
    renderer.resize(snapshot.entries.length);
    card.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });

    try {
      const replay = await preparePhysicsReplay({
        mapId: selectedMapDefinition.id,
        bodyCount: snapshot.entries.length,
        seed,
        eventsEnabled: snapshot.eventsEnabled,
      });
      if (currentPreparationId !== preparationId) return;
      engine = new MarbleRaceEngine(snapshot.entries, {
        mapSelection: selectedMapDefinition.id,
        winMode: snapshot.winMode,
        requestedRank: snapshot.requestedRank,
        eventsEnabled: snapshot.eventsEnabled,
        seed,
      }, replay);
      race.removeAttribute('aria-busy');
      camera.reset(engine.map);
      mapName.textContent = engine.map.title;
      rule.textContent = ruleCopy(snapshot.winMode, engine.targetRank);
      countdownMap.textContent = engine.map.title;
      countdownValue.textContent = '3';
      countdownEndsAt = performance.now() + 3000;
      lastFrameAt = performance.now();
      if (document.hidden) pauseRace();
      screenReaderStatus.textContent = '트랙 준비가 끝났습니다. 3초 뒤 레이스가 시작됩니다.';
      updateHud(performance.now(), true);
      requestAnimationFrame(() => renderer.resize(engine?.marbles.length ?? 2));
    } catch (reason) {
      if (currentPreparationId !== preparationId) return;
      race.hidden = true;
      race.removeAttribute('aria-busy');
      form.hidden = false;
      shell.classList.remove('race-running');
      renderParticipants();
      error.textContent = reason instanceof Error ? reason.message : '트랙을 준비하지 못했습니다. 다시 시도해 주세요.';
    }
  }

  function stopRaceAndEdit(focusTarget: 'names' | 'map' = 'names'): void {
    preparationId += 1;
    engine = null;
    clearMiniMapInspection();
    race.removeAttribute('aria-busy');
    paused = false;
    race.hidden = true;
    form.hidden = false;
    shell.classList.remove('race-running');
    resultPanel.hidden = true;
    card.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    if (focusTarget === 'map') {
      const selectedMap = form.querySelector<HTMLInputElement>('input[name="map"]:checked');
      selectedMap?.closest('.fate-map-option')?.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center',
      });
      selectedMap?.focus();
    } else {
      composerName.focus();
    }
  }

  function pauseRace(): void {
    if (!engine || engine.phase === 'complete' || paused) return;
    paused = true;
    clearMiniMapInspection();
    if (engine.phase === 'ready') pausedCountdownRemaining = Math.max(0, countdownEndsAt - performance.now());
    pauseOverlay.hidden = false;
  }

  function resumeRace(): void {
    if (!engine || !paused) return;
    paused = false;
    if (engine.phase === 'ready') countdownEndsAt = performance.now() + pausedCountdownRemaining;
    pauseOverlay.hidden = true;
    lastFrameAt = performance.now();
  }

  function updateEventBanner(now: number): void {
    if (!engine) return;
    const presentation = engine.eventPresentation(Math.max(1.5, speed * .8));
    if (presentation) {
      const key = `${presentation.event.id}:${presentation.phase}`;
      if (lastEventKey !== key) {
        lastEventKey = key;
        eventHoldUntil = now + 800;
      }
      eventBanner.hidden = false;
      eventBanner.classList.toggle('active', presentation.phase === 'active');
      requireElement<HTMLElement>('#fate-event-banner small').textContent = presentation.phase === 'announcing'
        ? `반전까지 ${Math.max(1, Math.ceil(presentation.countdown))}`
        : '반전 이벤트';
      requireElement<HTMLElement>('#fate-event-banner strong').textContent = presentation.event.label;
      requireElement<HTMLElement>('#fate-event-banner span').textContent = presentation.event.description;
    } else if (now >= eventHoldUntil) {
      eventBanner.hidden = true;
      lastEventKey = '';
    }
  }

  function trackStandingChanges(currentEngine: MarbleRaceEngine): void {
    currentEngine.currentOrder().forEach((marble, index) => {
      const rank = index + 1;
      const committed = committedStandingRanks.get(marble.id);
      if (committed === undefined) {
        committedStandingRanks.set(marble.id, rank);
        return;
      }
      if (committed === rank) {
        pendingStandingRanks.delete(marble.id);
        return;
      }
      const pending = pendingStandingRanks.get(marble.id);
      if (!pending || pending.rank !== rank) {
        pendingStandingRanks.set(marble.id, { rank, since: currentEngine.elapsed });
        return;
      }
      if (currentEngine.elapsed - pending.since < .18) return;
      standingRankChanges.set(marble.id, { delta: committed - rank, until: currentEngine.elapsed + 3 });
      committedStandingRanks.set(marble.id, rank);
      pendingStandingRanks.delete(marble.id);
    });
    for (const [id, change] of standingRankChanges) {
      if (change.until < currentEngine.elapsed) standingRankChanges.delete(id);
    }
  }

  function updateHud(now: number, force = false): void {
    if (!engine || !force && now - lastHudAt < 100) return;
    lastHudAt = now;
    time.textContent = `${engine.elapsed.toFixed(1)}초`;
    finished.textContent = `${engine.finishers.length} / ${engine.marbles.length}`;
    remaining.textContent = `${engine.remaining.length}개 남음`;
    const lapCount = engine.replay.lapCount ?? 1;
    const followed = engine.marbles.find((marble) => marble.id === latestSelection.highlightedIds[0]) ?? engine.currentOrder()[0];
    lapBadge.hidden = lapCount <= 1;
    if (lapCount > 1 && followed) {
      lapBadge.textContent = `추적 마블 ${followed.lap} / ${lapCount} 랩`;
      if (followed.lap > presentedLap && engine.phase === 'racing') {
        presentedLap = followed.lap;
        lapNoticeUntil = now + 2000;
        lapNotice.textContent = `${followed.lap === lapCount ? '마지막' : followed.lap} 랩 · ${marbleDisplayName(followed)}`;
      }
    }
    lapNotice.hidden = lapCount <= 1 || now >= lapNoticeUntil || engine.phase !== 'racing' || engine.result !== null;
    if (latestSelection.mode === 'winner') standingsTitle.textContent = '당첨 마블';
    else if (latestSelection.mode === 'rank-boundary') standingsTitle.textContent = `${ordinalCopy(engine.targetRank)} 경계`;
    else if (latestSelection.mode === 'last-pack') standingsTitle.textContent = '마지막 생존 경쟁';
    else standingsTitle.textContent = '선두 경쟁';

    trackStandingChanges(engine);
    const highlighted = new Set(latestSelection.highlightedIds);
    const rows = standingRowsForEngine(engine, latestSelection);
    const standingMarkup = rows.map((entry) => {
      const marble = engine!.marbles.find((candidate) => candidate.id === entry.id)!;
      const state = marble.finishedAt === null ? `${lapCount > 1 ? `${marble.lap}/${lapCount}랩 · ` : ''}${Math.round(marble.progress * 100)}%` : `${marble.finishRank}위 도착`;
      const rankChange = standingRankChanges.get(marble.id);
      const movement = rankChange
        ? `<em class="fate-rank-move ${rankChange.delta > 0 ? 'up' : 'down'}">${rankChange.delta > 0 ? '↑' : '↓'}${Math.abs(rankChange.delta)}</em>`
        : '';
      return `<div class="fate-standing-row${highlighted.has(marble.id) ? ' highlighted' : ''}${marble.finishedAt !== null ? ' finished' : ''}">
        <i style="--marble-color:${marble.color}">${marble.serial}</i><strong>${escapeHtml(marbleDisplayName(marble))}</strong>${movement}<span>${state}</span>
      </div>`;
    }).join('');
    if (standingMarkup !== lastStandingMarkup) {
      standingList.innerHTML = standingMarkup;
      lastStandingMarkup = standingMarkup;
    }

    if (engine.finishers.length !== lastFinishedCount) {
      const recent = engine.finishers.at(-1);
      if (recent && !resultVisible) screenReaderStatus.textContent = `${marbleDisplayName(recent)} 마블이 ${recent.finishRank}위로 도착했습니다.`;
      if (resultVisible) {
        resultPanel.querySelector('ol')!.innerHTML = resultFinishersMarkup(engine);
        const progress = resultPanel.querySelector('[data-result-progress]');
        if (progress) progress.textContent = resultProgressCopy(engine);
      }
      lastFinishedCount = engine.finishers.length;
    }
    updateEventBanner(now);
  }

  function verificationText(currentEngine: MarbleRaceEngine): string {
    return [
      `운명의 마블 추첨 번호: ${currentEngine.seed}`,
      `물리 엔진: ${currentEngine.replay.version}`,
      `맵: ${currentEngine.map.title} (${currentEngine.map.version})`,
      `물리 완주: ${currentEngine.replay.bodyCount - currentEngine.replay.forcedFinishCount} / ${currentEngine.replay.bodyCount}`,
      `리플레이 체크섬: ${currentEngine.replay.checksum}`,
    ].join('\n');
  }

  function showResult(): void {
    if (!engine || resultVisible || !engine.result) return;
    resultVisible = true;
    clearMiniMapInspection();
    const winner = engine.result;
    stage.classList.add('result-visible');
    resultPanel.hidden = false;
    resultPanel.innerHTML = `
      <small>${ordinalCopy(engine.targetRank)} 도착 · 당첨</small>
      <h2>${escapeHtml(winner.name)}</h2>
      <p>${winner.copyTotal > 1 ? `${escapeHtml(winner.name)} 마블 ${winner.copyIndex} / ${winner.copyTotal}` : `마블 #${winner.serial}`} · 추첨 번호 ${shortSeed(engine.seed)}</p>
      <ol>${resultFinishersMarkup(engine)}</ol>
      ${engine.map.revealOnTargetFinish ? `<p data-result-progress>${resultProgressCopy(engine)}</p>` : ''}
      <details class="fate-verification">
        <summary>추첨 검증 정보</summary>
        <dl>
          <div><dt>전체 추첨 번호</dt><dd>${escapeHtml(engine.seed)}</dd></div>
          <div><dt>물리 엔진</dt><dd>${escapeHtml(engine.replay.version)}</dd></div>
          <div><dt>맵 버전</dt><dd>${escapeHtml(engine.map.version)}</dd></div>
          <div><dt>물리 완주</dt><dd>${engine.replay.bodyCount - engine.replay.forcedFinishCount} / ${engine.replay.bodyCount}</dd></div>
          <div><dt>체크섬</dt><dd>${escapeHtml(engine.replay.checksum)}</dd></div>
        </dl>
      </details>
      <div class="fate-result-actions">
        <button type="button" data-result-action="again">같은 명단으로 다시</button>
        <button type="button" data-result-action="map">맵 선택해서 다시</button>
        <button type="button" data-result-action="edit">이름 수정</button>
        <button type="button" data-result-action="copy">검증 정보 복사</button>
      </div>
      <span>익명 물리 리플레이를 만든 뒤 이름을 배정해 모든 마블에 같은 확률을 적용해요.</span>`;
    if (engine.map.revealOnTargetFinish) resultPanel.scrollTop = 0;
    screenReaderStatus.textContent = `${ordinalCopy(engine.targetRank)} 도착 당첨자는 ${winner.name}입니다.`;
  }

  const frameProfile = import.meta.env.DEV && new URLSearchParams(location.search).has('profile') ? createFrameProfile() : null;
  function frame(now: number): void {
    const cpuStarted = frameProfile ? performance.now() : 0;
    const timing = raceFrameTiming(lastFrameAt, now);
    lastFrameAt = now;
    if (engine) {
      if (!paused && engine.phase === 'ready') {
        const remainingCountdown = Math.max(0, countdownEndsAt - now);
        countdownValue.textContent = String(Math.max(1, Math.ceil(remainingCountdown / 1000)));
        if (remainingCountdown <= 0) {
          engine.start();
          countdown.hidden = true;
          screenReaderStatus.textContent = '레이스가 시작됐습니다.';
        }
      } else if (!paused && engine.phase === 'racing') {
        const advance = advanceRaceFrame(engine, timing.realDelta, speed);
        if (advance.targetJustFinished) {
          if (advance.nextSpeed !== speed) {
            setSpeed(advance.nextSpeed);
            showNotice(`${ordinalCopy(engine.targetRank)} 도착 · 남은 마블 4×`, 1400);
          } else {
            showNotice(`${ordinalCopy(engine.targetRank)} 도착!`, 900);
          }
        }
        if (engine.result && resultReachedAt === null) {
          resultReachedAt = now;
          if (engine.finishers.length === engine.marbles.length) showNotice('모든 마블 도착!', 900);
        }
      }

      latestSelection = camera.update(engine, renderer.width, renderer.height, timing.realDelta, reduceMotion);
      const cpuUpdated = frameProfile ? performance.now() : 0;
      renderer.draw(engine, camera.view, {
        cameraSelection: latestSelection,
        cameraInspecting: camera.isInspecting,
        resultMarbleId: engine.finishers.length >= engine.targetRank ? engine.finishers[engine.targetRank - 1]?.id : undefined,
        reduceMotion,
      });
      const cpuRendered = frameProfile ? performance.now() : 0;
      updateHud(now);
      if (frameProfile && !paused && engine.phase === 'racing') frameProfile(`${engine.map.id}:${engine.marbles.length}:${engine.seed}:${speed}`, now, cpuUpdated - cpuStarted, cpuRendered - cpuUpdated, performance.now() - cpuRendered);
      if (resultReachedAt !== null && now - resultReachedAt >= (engine.map.revealOnTargetFinish ? 0 : 900)) showResult();
    }
    requestAnimationFrame(frame);
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    try {
      const snapshot = readSnapshot();
      error.textContent = '';
      void beginRace(snapshot);
    } catch (reason) {
      error.textContent = reason instanceof Error ? reason.message : '입력값을 확인해 주세요.';
    }
  });
  addParticipantButton.addEventListener('click', addParticipant);
  composerName.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    addParticipant();
  });
  form.querySelectorAll<HTMLButtonElement>('[data-composer-step]').forEach((button) => {
    button.addEventListener('click', () => {
      const next = Math.max(1, Math.min(MAX_MARBLE_COUNT, Number(composerQuantity.value || 1) + Number(button.dataset.composerStep)));
      composerQuantity.value = String(next);
      composerQuantity.setAttribute('aria-valuenow', String(next));
      setParticipantMessage('');
    });
  });
  const selectWholeNumber = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.dataset.selectAllNumber === undefined) return;
    target.select();
  };
  form.addEventListener('focusin', selectWholeNumber);
  form.addEventListener('click', selectWholeNumber);
  form.addEventListener('input', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.dataset.selectAllNumber === undefined) return;
    const digits = target.value.replace(/\D/g, '');
    if (target.value !== digits) target.value = digits;
    target.setAttribute('aria-valuenow', digits || '0');
    if (target.dataset.groupCount !== undefined && digits) {
      const index = Number(target.dataset.groupCount);
      const group = participantGroups[index];
      const count = Number(digits);
      if (group && Number.isInteger(count)) {
        try {
          participantGroups = updateParticipantGroup(participantGroups, index, { ...group, count });
        } catch {
          // Keep the last valid state until change/blur reports the invalid draft.
        }
      }
    }
  });
  participantList.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement).closest<HTMLButtonElement>('button');
    const row = (event.target as HTMLElement).closest<HTMLElement>('[data-participant-index]');
    if (!target || !row) return;
    const index = Number(row.dataset.participantIndex);
    const group = participantGroups[index];
    if (!group) return;
    if (target.dataset.groupRemove !== undefined) {
      replaceGroups(participantGroups.filter((_, candidate) => candidate !== index), `${group.name} 참가자를 삭제했어요.`);
      return;
    }
    if (target.dataset.groupStep !== undefined) {
      const nextCount = group.count + Number(target.dataset.groupStep);
      if (nextCount < 1) return;
      updateGroupAt(index, { ...group, count: nextCount });
    }
  });
  participantList.addEventListener('change', (event) => {
    const target = event.target as HTMLInputElement;
    const row = target.closest<HTMLElement>('[data-participant-index]');
    if (!row) return;
    const index = Number(row.dataset.participantIndex);
    const group = participantGroups[index];
    if (!group) return;
    if (target.dataset.groupName !== undefined) updateGroupAt(index, { ...group, name: target.value }, `${target.value.trim()} 이름으로 수정했어요.`);
    if (target.dataset.groupCount !== undefined) updateGroupAt(index, { ...group, count: Number(target.value) });
  });
  participantList.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    (event.target as HTMLInputElement).blur();
  });
  form.querySelectorAll<HTMLButtonElement>('[data-all-count]').forEach((button) => {
    button.addEventListener('click', () => {
      try {
        const count = Number(button.dataset.allCount);
        replaceGroups(setAllParticipantCounts(participantGroups, count), `모든 참가자의 마블을 ${count}개로 맞췄어요.`);
      } catch (reason) {
        setParticipantMessage(reason instanceof Error ? reason.message : '수량을 적용할 수 없어요.', 'error');
      }
    });
  });
  requireElement<HTMLButtonElement>('#fate-open-paste').addEventListener('click', () => {
    pasteSheet.hidden = false;
    renderPastePreview();
    pasteInput.focus();
  });
  requireElement<HTMLButtonElement>('#fate-close-paste').addEventListener('click', closePasteSheet);
  requireElement<HTMLButtonElement>('#fate-cancel-paste').addEventListener('click', closePasteSheet);
  pasteInput.addEventListener('input', renderPastePreview);
  applyPasteButton.addEventListener('click', () => {
    if (!pasteGroups) return;
    try {
      let next = participantGroups;
      let lastMerged = -1;
      for (const group of pasteGroups) {
        const merged = mergeParticipantGroup(next, group);
        next = merged.groups;
        lastMerged = merged.mergedIndex;
      }
      const addedNames = pasteGroups.length;
      replaceGroups(next, `${addedNames}명의 명단을 목록에 반영했어요.`, lastMerged);
      closePasteSheet();
    } catch (reason) {
      pastePreview.classList.add('invalid');
      pastePreview.textContent = reason instanceof Error ? reason.message : '명단을 추가할 수 없어요.';
    }
  });
  rankInput.addEventListener('input', () => renderParticipants());
  const startHint = requireElement<HTMLElement>('#fate-start-hint');
  function updateStartHint(): void {
    startHint.textContent = mapStartHint(selectedMap());
  }
  updateStartHint();
  form.addEventListener('change', (event) => {
    const target = event.target as HTMLInputElement;
    if (target.name === 'win-mode') {
      rankField.hidden = selectedWinMode() !== 'rank';
      renderParticipants();
    }
    if (target.name === 'map') updateStartHint();
  });
  speedButton.addEventListener('click', cycleSpeed);
  canvas.addEventListener('pointermove', (event) => {
    if (event.pointerType === 'touch') return;
    updateMiniMapInspection(event);
  });
  canvas.addEventListener('pointerleave', (event) => {
    if (event.pointerType !== 'touch') clearMiniMapInspection();
  });
  requireElement<HTMLButtonElement>('#fate-pause').addEventListener('click', pauseRace);
  requireElement<HTMLButtonElement>('#fate-resume').addEventListener('click', resumeRace);
  requireElement<HTMLButtonElement>('#fate-back').addEventListener('click', () => stopRaceAndEdit());
  requireElement<HTMLButtonElement>('#fate-fullscreen').addEventListener('click', () => {
    if (!document.fullscreenElement) void card.requestFullscreen().catch(() => showNotice('전체 화면을 열 수 없어요.'));
    else void document.exitFullscreen();
  });
  resultPanel.addEventListener('click', (event) => {
    const action = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-result-action]')?.dataset.resultAction;
    if (!action || !lastSnapshot) return;
    if (action === 'edit') {
      stopRaceAndEdit();
      return;
    }
    if (action === 'copy' && engine) {
      if (!navigator.clipboard) {
        showNotice('이 브라우저에서는 자동 복사를 지원하지 않아요.');
        return;
      }
      void navigator.clipboard.writeText(verificationText(engine))
        .then(() => showNotice('검증 정보를 복사했어요.'))
        .catch(() => showNotice('복사하지 못했어요. 검증 정보를 직접 선택해 주세요.'));
      return;
    }
    if (action === 'again') {
      void beginRace(lastSnapshot);
      return;
    }
    if (action === 'map') {
      stopRaceAndEdit('map');
    }
  });
  window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !pasteSheet.hidden) {
      event.preventDefault();
      closePasteSheet();
      requireElement<HTMLButtonElement>('#fate-open-paste').focus();
      return;
    }
    if ((event.target as HTMLElement).closest('input, textarea, select, [contenteditable="true"]')) return;
    if (!engine || race.hidden) return;
    const shortcut = resolveRaceShortcut(event);
    if (shortcut === 'speed') {
      if (engine.phase !== 'racing' || paused) return;
      event.preventDefault();
      cycleSpeed();
      return;
    }
    if (shortcut === 'pause') {
      if (engine.phase === 'complete') return;
      event.preventDefault();
      if (paused) resumeRace();
      else pauseRace();
    }
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) clearMiniMapInspection();
    if (document.hidden && engine && engine.phase !== 'complete') pauseRace();
  });
  window.addEventListener('blur', clearMiniMapInspection);
  window.addEventListener('resize', () => {
    clearMiniMapInspection();
    renderer.resize(engine?.marbles.length ?? 2);
  });
  document.addEventListener('fullscreenchange', () => {
    clearMiniMapInspection();
    renderer.resize(engine?.marbles.length ?? 2);
  });

  renderParticipants();
  renderPastePreview();
  requestAnimationFrame(frame);
}
